import { BASE_V1, corpsV1, type DocumentV1 } from './gabarits-v1.ts';
import { ApiError, type ClientApi } from './api.ts';
/**
 * Impression : un document construit pour le papier.
 *
 * ── Pourquoi une fenêtre, et non une feuille de style ───────────────────────
 * On imprimait la page elle-même, avec une feuille `@media print` qui masquait
 * ce qui ne devait pas sortir. C'est économe, et c'était insuffisant : ce qui
 * arrivait sur le papier ne ressemblait pas à l'écran, et du contenu manquait.
 *
 * La raison est structurelle. Les cellules de nos tableaux ne contiennent pas
 * du texte mais des CHAMPS DE SAISIE. Un `<textarea>` n'imprime que ce qui
 * tenait dans sa hauteur : le reste disparaît, sans un signe. On peut placer à
 * côté un jumeau invisible portant le même texte — c'est ce qu'on faisait —
 * mais il faut alors le tenir d'accord avec le champ, dans chaque composant,
 * pour toujours. Un oubli quelque part, et la feuille ment.
 *
 * La v1 avait tranché autrement, et bien : au clic, elle CLONE le tableau
 * affiché, remplace chaque champ par son texte, retire les commandes, puis
 * ouvre un document fait pour le papier. Le tableau imprimé est alors le
 * tableau de l'écran — mêmes colonnes, mêmes lignes, même ordre — et tout son
 * contenu y figure, puisqu'il n'est plus enfermé dans une zone de saisie.
 *
 * ── Ce que ce fichier NE fait pas ───────────────────────────────────────────
 * Il ne met pas en forme les données ni ne les recalcule. Il transforme un
 * fragment de page déjà rendu. Une feuille qui recalculerait ce qu'elle
 * affiche finirait par diverger de l'écran — c'est précisément le défaut qu'on
 * corrige.
 */

/**
 * Nombre maximal de colonnes de contenu par feuille.
 *
 * Cinq, plus la colonne d'intitulé reprise sur chaque bloc, tiennent
 * lisiblement sur un A4 paysage. Au-delà, chaque colonne descend sous les vingt
 * millimètres et la feuille cesse d'être un document qu'on pose sur une table.
 */
export const COLONNES_PAR_FEUILLE = 5;

/**
 * Découpe les colonnes en blocs imprimables.
 *
 * ── Pourquoi seulement AU-DELÀ de six ───────────────────────────────────────
 * Un tableau étroit tient sur une seule feuille, et le découper produirait une
 * deuxième page presque vide. Le seuil est donc au-dessus de la capacité d'une
 * feuille, pas à égalité : à six colonnes on préfère serrer un peu plutôt que
 * d'ajouter une page pour la sixième.
 *
 * Renvoie un tableau VIDE quand rien n'est à découper.
 */
export function tranchesImpression<T>(colonnes: readonly T[]): T[][] {
  if (colonnes.length <= COLONNES_PAR_FEUILLE + 1) return [];
  const tranches: T[][] = [];
  for (let i = 0; i < colonnes.length; i += COLONNES_PAR_FEUILLE) {
    tranches.push(colonnes.slice(i, i + COLONNES_PAR_FEUILLE));
  }
  return tranches;
}

/** Titre d'un bloc : « Colonnes 2/3 ». */
export function titreDeBloc(index: number, total: number): string {
  return `Colonnes ${index + 1}/${total}`;
}

/**
 * Aire utile d'une feuille A4, en pixels CSS (96 ppp), marges de 10 mm ôtées.
 *
 * 210 × 297 mm, moins 20 mm de marges dans chaque sens, à 96 pixels par pouce.
 */
const FEUILLE = {
  portrait: { largeur: 718, hauteur: 1047 },
  paysage: { largeur: 1047, hauteur: 718 },
} as const;

/** Place prise en haut du document par son titre, filet compris. */
const HAUTEUR_DU_TITRE = 48;

/**
 * Marge de sécurité.
 *
 * La hauteur du titre est une estimation, et le navigateur dessine ses propres
 * en-têtes — date, adresse, numéro de page — dans la marge. Deux pour cent
 * suffisent à absorber l'écart, et coûtent bien moins qu'un créneau coupé.
 */
const SECURITE = 0.98;

/**
 * Comment poser sur UNE feuille un fragment qui ne se coupe pas.
 *
 * ── Pourquoi certains fragments ne peuvent pas être coupés ──────────────────
 * Un tableau se coupe proprement entre deux lignes : la deuxième feuille
 * reprend l'en-tête et la lecture continue. Le planning de l'emploi du temps,
 * lui, n'est pas fait de lignes : c'est une journée dessinée à l'échelle, où
 * chaque créneau est POSITIONNÉ à son heure. La page s'arrête où elle tombe —
 * signalé à 14 h 30, en plein milieu des créneaux de l'après-midi, dont la
 * moitié haute était sur une feuille et la moitié basse sur l'autre.
 *
 * Le déplacer n'aurait aucun sens : sa position EST son horaire. La seule
 * réponse juste est de faire tenir la journée entière sur une feuille.
 *
 * ── Seule la HAUTEUR commande la réduction ──────────────────────────────────
 * La largeur de l'écran n'a rien à dire ici. Le planning occupe la place qu'on
 * lui donne : mesuré à 1 108 px sur un écran de 1 440, il en ferait 1 800 sur
 * un écran plus large, sans que son contenu change. Réduire à cette
 * proportion-là aurait donné 0,63 sur une feuille — un libellé de créneau sous
 * cinq points — pour une raison qui ne tient qu'au bureau de qui imprime.
 *
 * On lui donne donc la largeur de la FEUILLE, où ses cinq colonnes se
 * répartissent d'elles-mêmes, et on ne réduit que si la journée est trop haute.
 * La largeur renvoyée est celle à poser AVANT réduction : réduite d'autant,
 * elle retombe exactement sur la largeur utile du papier.
 */
/**
 * Hauteur qu'occupera le fragment, mesurée coûte que coûte.
 *
 * ── Pourquoi l'original ne suffit pas ───────────────────────────────────────
 * L'emploi du temps a deux formes : le planning au-delà de 768 px, une liste
 * par jour en dessous — une grille de cinq colonnes sur un téléphone est
 * illisible. Sur papier, la question ne se pose pas : une feuille A4 a la
 * largeur d'un grand écran, et c'est donc toujours le planning qui s'imprime.
 *
 * Mais depuis une fenêtre étroite, le planning n'est pas rendu : il mesure
 * zéro, la réduction ne s'applique pas, et le créneau de l'après-midi se
 * retrouve coupé en deux — le défaut qu'on venait de corriger.
 *
 * On mesure alors la COPIE, posée hors du champ à la largeur qu'elle aura sur
 * la feuille. Elle est retirée aussitôt : une copie oubliée dans la page
 * fausserait tout ce qui suit.
 */
export function hauteurAImprimer(
  source: HTMLElement,
  copie: HTMLElement,
  orientation: 'portrait' | 'paysage' = 'portrait',
): number {
  const affichee = source.getBoundingClientRect().height;
  if (affichee > 0) return affichee;

  const bac = source.ownerDocument.createElement('div');
  bac.dataset['mesureImpression'] = '';
  bac.setAttribute(
    'style',
    `position: absolute; left: -10000px; top: 0; visibility: hidden;`
    + ` width: ${FEUILLE[orientation].largeur}px;`,
  );
  const jumeau = copie.cloneNode(true) as HTMLElement;
  bac.appendChild(jumeau);
  source.ownerDocument.body.appendChild(bac);
  const hauteur = jumeau.getBoundingClientRect().height;
  bac.remove();
  return hauteur;
}

export function tenueSurUneFeuille(
  hauteur: number,
  orientation: 'portrait' | 'paysage' = 'portrait',
): { zoom: number; largeur: number } {
  const feuille = FEUILLE[orientation];
  const disponible = (feuille.hauteur - HAUTEUR_DU_TITRE) * SECURITE;
  // Une hauteur nulle veut dire « non mesurable » — hors écran, ou pas encore
  // rendu. Réduire au hasard ferait pire que ne rien faire. On ne grossit
  // jamais non plus : une petite grille étirée à la feuille serait grotesque.
  const zoom = hauteur > 0 ? Math.min(1, disponible / hauteur) : 1;
  return { zoom: Number(zoom.toFixed(3)), largeur: Math.round(feuille.largeur / zoom) };
}

// ── Transformation d'un fragment pour le papier ──────────────────────────────

/**
 * Remplace un champ par un bloc de texte portant sa valeur.
 *
 * `white-space: pre-wrap` : un déroulement de séance est saisi en plusieurs
 * lignes, et les écraser en un pavé le rend inutilisable devant une classe.
 * `word-break` : un mot plus large que sa colonne déborderait sur la voisine.
 */
function blocTexte(document_: Document, texte: string): HTMLElement {
  const bloc = document_.createElement('div');
  bloc.setAttribute(
    'style',
    'white-space: pre-wrap; word-break: break-word; min-height: 1em;',
  );
  bloc.textContent = texte;
  return bloc;
}

/**
 * Prépare une copie imprimable d'un fragment de page.
 *
 * ── Pourquoi lire les valeurs sur l'ORIGINAL ────────────────────────────────
 * `cloneNode` ne copie pas la valeur COURANTE d'un champ : il copie ce que
 * portait le HTML de départ. Sur une application où tout est saisi après le
 * rendu, la copie serait donc vide — précisément le « contenu qui manque ».
 *
 * Les deux arbres ayant la même forme, `querySelectorAll` les parcourt dans le
 * même ordre : on apparie par rang, et l'on prend la valeur du champ vivant.
 */
export function preparerPourImpression(source: HTMLElement): HTMLElement {
  const copie = source.cloneNode(true) as HTMLElement;
  const document_ = source.ownerDocument;

  const champs = 'textarea, input, select';
  const originaux = [...source.querySelectorAll(champs)];
  const copies = [...copie.querySelectorAll(champs)];

  copies.forEach((clone, rang) => {
    const vivant = originaux[rang];
    let texte = '';

    if (vivant instanceof HTMLInputElement) {
      // Une case à cocher ne se lit pas par sa valeur : c'est son ÉTAT qui
      // porte l'information, et sur le papier il devient une marque.
      texte = vivant.type === 'checkbox' ? (vivant.checked ? '✔' : '') : vivant.value;
    } else if (vivant instanceof HTMLTextAreaElement) {
      texte = vivant.value;
    } else if (vivant instanceof HTMLSelectElement) {
      // Le libellé choisi, pas la valeur : un identifiant technique imprimé ne
      // dit rien à personne.
      texte = vivant.selectedOptions[0]?.text ?? '';
    }

    clone.replaceWith(blocTexte(document_, texte));
  });

  /*
   * Les commandes disparaissent — sauf celles qui SONT le contenu.
   *
   * Dans la grille d'évaluation, chaque case de notation est un bouton : son
   * libellé (« A », « D ») est la note elle-même. Les retirer viderait le
   * tableau. La classe `imprimable` marque cette exception, déjà utilisée par
   * la feuille de style.
   */
  copie.querySelectorAll('button:not(.imprimable)').forEach((b) => b.remove());
  copie.querySelectorAll('button.imprimable').forEach((bouton) => {
    /*
     * On retire la QUALITÉ DE COMMANDE, pas la forme.
     *
     * Le bouton devenait un bloc de texte nu. Cela suffisait à la grille
     * d'évaluation, dont les notes sont des lettres ; pas au planning de
     * l'emploi du temps, où le créneau est un pavé POSITIONNÉ : sa place dans
     * la journée tient à son style, et le remplacer par du texte effondrait le
     * planning en une liste. Classe et style suivent donc, ainsi que le
     * contenu interne — un créneau porte son horaire et sa matière sur deux
     * lignes.
     */
    const bloc = document_.createElement('div');
    for (const attribut of ['class', 'style']) {
      const valeur = bouton.getAttribute(attribut);
      if (valeur !== null) bloc.setAttribute(attribut, valeur);
    }
    bloc.innerHTML = bouton.innerHTML;
    bouton.replaceWith(bloc);
  });

  // Les jumeaux d'impression feraient doublon : le champ est déjà devenu texte.
  copie.querySelectorAll('.impression-texte:not(.impression-seule)').forEach((e) => e.remove());

  /*
   * Sauf le jumeau qui est SEUL à porter le texte : `impression-seule`.
   *
   * L'horaire du cahier journal est saisi dans deux champs `time`, masqués à
   * l'impression parce qu'ils s'y liraient « 08:45 » sur deux lignes. Son
   * jumeau « 8h45 – 9h45 » était retiré comme les autres : la colonne Horaire
   * sortait VIDE. Il est désormais rendu visible, et les champs partent.
   */
  copie.querySelectorAll('.impression-seule').forEach((e) => e.classList.remove('hidden'));

  /*
   * Les poignées de redimensionnement partent, et ELLES SEULES.
   *
   * On retirait ici tout ce qui portait `aria-hidden="true"`. C'était trop
   * large : cet attribut marque ce qu'un lecteur d'écran doit ignorer, pas ce
   * qui est décoratif. La colonne des heures de l'emploi du temps le porte —
   * ses graduations sont redondantes avec les horaires annoncés dans chaque
   * créneau — et elle disparaissait de la feuille, laissant un planning sans
   * échelle de temps.
   */
  copie.querySelectorAll('.poignee-colonne, .print\\:hidden').forEach((e) => e.remove());

  /*
   * Les colonnes retirées ne gardent pas leur place.
   *
   * Duplication et Suppression partent avec leurs boutons, mais le `colgroup`
   * gardait leurs deux `col` : le tableau en comptait sept pour cinq cellules,
   * et un quart de la feuille restait blanc à droite pendant que le
   * déroulement s'étranglait. On ramène le `colgroup` au nombre de cellules
   * de la première ligne — les colonnes retirées sont toujours les dernières.
   */
  for (const table of copie.querySelectorAll('table')) {
    const premiere = table.querySelector('tr');
    const groupe = table.querySelector('colgroup');
    if (!premiere || !groupe) continue;
    const largeur = [...premiere.children]
      .reduce((n, c) => n + (Number((c as HTMLTableCellElement).colSpan) || 1), 0);
    [...groupe.querySelectorAll('col')].slice(largeur).forEach((c) => c.remove());
  }

  return copie;
}

/**
 * Réduit un tableau aux colonnes d'un bloc, en gardant la première.
 *
 * La colonne d'intitulé — semaine, élève, sous-domaine — est reprise sur chaque
 * feuille : sans elle, la deuxième page est une grille de valeurs dont on ne
 * sait plus à quoi elles se rapportent.
 */
export function reduireAuxColonnes(table: HTMLTableElement, indices: number[]): HTMLTableElement {
  const garder = new Set([0, ...indices]);
  const copie = table.cloneNode(true) as HTMLTableElement;

  const filtrer = (parent: Element, selecteur: string) => {
    [...parent.querySelectorAll(selecteur)].forEach((cellule, rang) => {
      if (!garder.has(rang)) cellule.remove();
    });
  };

  const colgroup = copie.querySelector('colgroup');
  if (colgroup) filtrer(colgroup, 'col');
  for (const ligne of copie.querySelectorAll('tr')) filtrer(ligne, 'th, td');

  return copie;
}

/**
 * Récupère les feuilles de style de l'application.
 *
 * ── Pourquoi les emporter ───────────────────────────────────────────────────
 * Le document d'impression était habillé d'un style écrit à part, à la manière
 * de la v1. Cela marchait pour ses tableaux, faits de balises nues. Nos écrans
 * ne le sont pas : leur forme tient à des classes utilitaires, et le PLANNING
 * de l'emploi du temps n'est même pas un tableau — c'est un empilement de blocs
 * positionnés. Cloné sans son style, il n'en restait rien.
 *
 * Emporter la feuille de l'application résout cela d'un coup, et répond à la
 * demande telle qu'elle a été formulée : ce qui est à l'écran doit se retrouver
 * sur le papier, dans la même forme.
 *
 * Bénéfice de bord : les règles `@media print` déjà écrites — orientation
 * paysage, neutralisation des largeurs d'écran, en-tête répété — voyagent avec
 * elle et s'appliquent au moment d'imprimer.
 */
function stylesDeLApplication(document_: Document): string {
  return [...document_.querySelectorAll('link[rel="stylesheet"], style')]
    .map((noeud) => (noeud instanceof HTMLLinkElement
      ? `<link rel="stylesheet" href="${noeud.href}">`
      : `<style>${noeud.textContent ?? ''}</style>`))
    .join('\n');
}

/**
 * La couche PAPIER, posée après le style de l'application.
 *
 * Volontairement mince : elle ne redessine rien, elle défait ce qui n'a pas de
 * sens sur une feuille — un cadre qui défile, une colonne qui flotte, une ombre
 * portée — et pose le titre du document.
 */
const STYLE_PAPIER = `
  body { background: #fff; padding: 0; }
  h1.titre-impression { font-size: 13pt; font-weight: 700; margin-bottom: 4mm;
       border-bottom: 2px solid #1a1a1a; padding-bottom: 2mm; }
  h2.titre-bloc { font-size: 10pt; font-weight: 700; margin: 5mm 0 2mm; }
  /* Sur papier il n'y a pas de barre de défilement : ce qui dépassait était
     simplement absent de la feuille. */
  [class*='overflow-'] { overflow: visible !important; max-height: none !important; }
  /* Une colonne collante flotte par-dessus les autres sur le papier. */
  .sticky { position: static !important; }
  * { box-shadow: none !important; }
  thead { display: table-header-group; }

  /*
   * ── Une ligne PEUT se couper ────────────────────────────────────────────
   *
   * On l'interdisait, pour qu'une ligne ne se retrouve pas à cheval sur deux
   * feuilles. C'est un bon réflexe pour une liste d'élèves, dont les lignes
   * font trois millimètres. Ce n'en est pas un pour une programmation, dont
   * une ligne porte tous les blocs d'un sous-domaine sur cinq périodes :
   * mesuré à 1 289 px pour 718 px de hauteur utile en A4 paysage.
   *
   * Le navigateur ne peut alors pas honorer l'interdiction — il repousse la
   * ligne entière à la feuille suivante, puis la coupe quand même. Résultat
   * signalé : une première page ne portant que le titre et les en-têtes de
   * colonnes, et tout le contenu à partir de la deuxième.
   *
   * L'en-tête se répétant en haut de chaque feuille, une ligne coupée reste
   * lisible.
   *
   * ── Et RIEN d'autre ne doit refuser la coupure ──────────────────────────
   * Une première version protégeait les blocs d'une cellule, pour qu'une
   * coupure tombe à leurs jointures. Elle reproduisait le défaut d'un cran plus
   * bas : mesuré, le premier bloc de la programmation fait 718 px — toute la
   * hauteur d'une page — parce qu'un enseignant peut y coller ce qu'il veut.
   * Ne pouvant y tenir, il était repoussé, et la ligne avec lui.
   *
   * Interdire la coupure n'est sûr que sur un contenu dont on sait qu'il tient
   * sur une feuille. Ici, rien ne le garantit : c'est de la saisie libre.
   */
  tr, td > div > * { break-inside: auto; }

  .saut { break-before: page; }

  /*
   * Les FONDS s'impriment.
   *
   * Le navigateur les écarte par défaut, pour épargner l'encre. C'est un bon
   * réglage pour une page web ; ici il efface de l'information : un créneau
   * d'emploi du temps est un pavé coloré portant du texte clair, et sans son
   * fond il ne reste rien à lire. La couleur de la matière est d'ailleurs ce
   * qui rend un planning lisible d'un coup d'œil.
   */
  *, *::before, *::after {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /*
   * ── Les largeurs de CONFORT de l'écran ne sortent pas sur la feuille ──────
   *
   * Les colonnes sont réglables à la poignée, et la largeur choisie est posée
   * en style *inline* sur l'en-tête : width, min-width et max-width. Une
   * colonne élargie pour relire un déroulement écraserait les autres sur le
   * papier. À l'écran on élargit ce qu'on lit sur le moment ; sur la feuille
   * on veut tout lire.
   *
   * Ces règles vivaient dans la feuille héritée, dont ce document s'écarte
   * désormais : elles voyagent avec lui.
   */
  table { table-layout: fixed; }
  col { width: auto !important; }
  col:first-child { width: 9% !important; }
  thead th[style] {
    width: auto !important;
    min-width: 0 !important;
    max-width: none !important;
  }
  th, td { overflow-wrap: anywhere; }
`;

/**
 * Le document se lance lui-même.
 *
 * Écrit DANS la fenêtre fille, et non appelé depuis l'application : c'est ce
 * déplacement qui empêche l'application de se figer le temps de l'aperçu. Voir
 * le commentaire au pied de `imprimer`.
 *
 * Elle se REFERME après l'aperçu, qu'on ait imprimé ou annulé : restée ouverte,
 * elle recouvrait l'application — entièrement quand celle-ci tourne dans une
 * fenêtre sans onglets. Correction reprise de la v1.
 *
 * La balise fermante est coupée en deux : écrite d'un seul tenant dans un
 * fichier TypeScript, elle refermerait le script de la page qui contient cette
 * chaîne au moment où un outil l'insère dans du HTML.
 */
const AUTO_IMPRESSION = '<script>'
  + 'addEventListener("afterprint", function () { setTimeout(function () { close(); }, 100); });'
  + 'addEventListener("load", function () {'
  + ' setTimeout(function () { focus(); print(); }, 300); });'
  + '</scr' + 'ipt>';

/**
 * Ouvre le document dans une fenêtre DÉTACHÉE de l'application.
 *
 * ── Pourquoi l'application se figeait ───────────────────────────────────────
 * Premier défaut : on appelait `print()` sur la fenêtre fille depuis la pile
 * d'exécution de l'application. Cet appel est synchrone — il ne rend la main
 * qu'une fois la boîte d'impression refermée. Le document porte donc désormais
 * son propre lancement.
 *
 * Cela n'a pas suffi, et la raison est ailleurs : une fenêtre ouverte par
 * `window.open` reste RATTACHÉE à celle qui l'ouvre — même groupe de contextes,
 * donc même processus de rendu. La boîte d'impression y ouvre une boucle
 * d'événements imbriquée qui gèle tout le processus, l'application comprise.
 *
 * `noopener` place la fenêtre dans un groupe de contextes distinct, ce qui
 * autorise le navigateur à lui donner son propre processus. Le document passe
 * alors par une adresse `blob:` — c'est le seul moyen de lui transmettre du
 * contenu sans garder la référence que `noopener` supprime précisément.
 *
 * ── Ce que cela coûte ───────────────────────────────────────────────────────
 * `window.open` avec `noopener` renvoie TOUJOURS `null` : on ne peut donc plus
 * distinguer une fenêtre bloquée d'une fenêtre ouverte. Le message « votre
 * navigateur a bloqué la fenêtre » ne peut plus être déclenché avec certitude.
 * On le remplace par une vérification de ce qui reste vérifiable : la création
 * de l'adresse elle-même.
 */
function ouvrirDetachee(html: string): boolean {
  let adresse: string;
  try {
    adresse = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  } catch {
    return false;
  }
  globalThis.open(adresse, '_blank', 'noopener');
  /*
   * L'adresse est révoquée plus tard, jamais tout de suite : la fenêtre doit
   * avoir eu le temps de la charger. Une minute laisse de la marge à un poste
   * lent, et l'objet est de toute façon libéré en quittant la page.
   */
  globalThis.setTimeout(() => URL.revokeObjectURL(adresse), 60_000);
  return true;
}

export interface DemandeImpression {
  titre: string;
  /** Fragments à imprimer, dans l'ordre. Chacun est copié et transformé. */
  sources: HTMLElement[];
  orientation?: 'portrait' | 'paysage';
  /** Style propre au module, ajouté après le style commun. */
  style?: string;
  /**
   * Découpage : nombre de colonnes de contenu du tableau, s'il faut le répartir
   * sur plusieurs feuilles. Absent, le tableau part d'un seul tenant.
   */
  colonnes?: number;
  /**
   * Réduit le fragment pour qu'il tienne sur une seule feuille.
   *
   * Pour ce qui ne se coupe pas entre deux lignes : le planning de l'emploi du
   * temps, où chaque créneau est positionné à son heure. Voir `echelleDeTenue`.
   */
  tenirSurUneFeuille?: boolean;
  /**
   * Document au modèle v1 (`lib/gabarits-v1.ts`) : il porte sa propre mise en
   * forme, et la couche papier des copies d'écran — largeurs de colonnes,
   * tableaux fixes — la contrarierait.
   */
  sansPapier?: boolean;
  /**
   * Ouvre le document construit. Injectable pour les tests.
   *
   * Reçoit le HTML plutôt qu'un nom de fenêtre : c'est le document qui compte,
   * et la façon de l'ouvrir a changé une fois déjà.
   */
  ouvrir?: (html: string) => boolean;
}

/**
 * Construit le document et lance l'impression.
 *
 * Renvoie `false` si la fenêtre n'a pas pu s'ouvrir — un bloqueur de fenêtres
 * surgissantes, le plus souvent. L'appelant le DIT à l'utilisateur : sans quoi
 * le bouton Imprimer ne fait rien, et rien n'explique pourquoi.
 */
export function imprimer(demande: DemandeImpression): boolean {
  return (demande.ouvrir ?? ouvrirDetachee)(assembler(demande, corpsImprime(demande)));
}

/** Le corps du document — titre et fragments préparés —, sans l'enveloppe. */
export function corpsImprime(demande: Omit<DemandeImpression, 'ouvrir'>): string {
  const morceaux: string[] = [];
  for (const source of demande.sources) {
    const prete = preparerPourImpression(source);
    const table = prete instanceof HTMLTableElement
      ? prete
      : prete.querySelector('table');

    const tranches = demande.colonnes
      ? tranchesImpression([...Array(demande.colonnes).keys()])
      : [];

    if (table && tranches.length > 0) {
      // Les indices sont décalés de 1 : la colonne 0 est celle des intitulés,
      // reprise sur chaque feuille.
      tranches.forEach((tranche, index) => {
        const bloc = reduireAuxColonnes(table, tranche.map((c) => c + 1));
        morceaux.push(
          `<h2 class="titre-bloc${index > 0 ? ' saut' : ''}">`
          + `${titreDeBloc(index, tranches.length)}</h2>`
          + bloc.outerHTML,
        );
      });
    } else if (demande.tenirSurUneFeuille) {
      /*
       * La mesure se prend sur l'ORIGINAL, seul à être rendu : la copie n'est
       * pas dans le document et n'a donc aucune dimension.
       */
      const orientation = demande.orientation ?? 'portrait';
      const { zoom, largeur } = tenueSurUneFeuille(
        hauteurAImprimer(source, prete, orientation),
        orientation,
      );
      /*
       * `zoom` et non `transform: scale` : le zoom réduit la PLACE PRISE en
       * même temps que le dessin. Une mise à l'échelle par transformation
       * laisserait le bloc occuper sa hauteur d'origine, et la feuille se
       * couperait au même endroit avec, en prime, du blanc en dessous.
       */
      morceaux.push(
        `<div style="width: ${largeur}px; zoom: ${zoom}">${prete.outerHTML}</div>`,
      );
    } else {
      morceaux.push(prete.outerHTML);
    }
  }

  return `<h1 class="titre-impression">${echapper(demande.titre)}</h1>`
    + morceaux.join('\n');
}

const echapper = (t: string) =>
  t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Le document complet : feuilles de l'application, couche papier, corps. */
function assembler(
  demande: Pick<DemandeImpression, 'titre' | 'orientation' | 'style' | 'sansPapier'>,
  corps: string,
  { feuilles = stylesDeLApplication(document), script = AUTO_IMPRESSION } = {},
): string {
  return [
    /*
     * ── Deux attributs qui décident de tout le reste ────────────────────────
     *
     * `data-theme="light"` : une feuille de papier est blanche. Sans cela le
     * document suit le thème du SYSTÈME, et en thème sombre `--color-fg` vaut
     * un gris très clair — mesuré à `rgb(230, 233, 238)` sur le fond blanc du
     * papier, contraste 1,09. Le texte disparaît, et le défaut ne se voit pas
     * sur une machine réglée en clair.
     *
     * `data-document-imprime` : ce document emporte les feuilles de
     * l'application pour en avoir la forme, y compris ce qu'elles disent de la
     * PAGE de l'application à l'impression. Or celles-ci ont été écrites pour
     * l'ancien mécanisme : `.impression-paysage { page: paysage }` y désigne
     * une page nommée, et comme le titre n'en est pas, le navigateur ouvrait
     * une feuille pour chacun — d'où une première page ne portant que le
     * titre. Le marqueur permet à ces règles de s'écarter de ce document, sans
     * cesser de servir quand on imprime l'application elle-même.
     */
    '<!DOCTYPE html><html lang="fr" data-theme="light" data-document-imprime>'
    + '<head><meta charset="UTF-8">'
    + `<title>${echapper(demande.titre)}</title>`
    // L'ordre compte : le style de l'application d'abord, la couche papier
    // ensuite, sans quoi la seconde serait écrasée par la première.
    + feuilles
    + '<style>'
    + `@page { size: A4 ${demande.orientation === 'paysage' ? 'landscape' : 'portrait'};`
    + ' margin: 10mm; }'
    + (demande.sansPapier ? '' : STYLE_PAPIER) + (demande.style ?? '')
    + '</style></head><body>'
    + corps
    + script
    + '</body></html>',
  ].join('');
}

/**
 * Imprime un document CONSTRUIT, et non copié de l'écran.
 *
 * Pour la fiche de préparation : on imprime plusieurs séquences à la fois,
 * chacune avec sa page de garde et toutes ses séances, quand l'écran n'en
 * montre qu'une, et une seule séance. Il n'y a donc rien à cloner — le document
 * est écrit à partir des données, dans la même enveloppe que les autres
 * impressions : feuilles de l'application, thème clair, fonds imprimés.
 *
 * Le corps arrive ÉCHAPPÉ par l'appelant : ce fichier ne sait pas ce qui, dans
 * ce HTML, est du texte saisi.
 */
export function imprimerDocument(
  demande: Pick<DemandeImpression, 'titre' | 'orientation' | 'style' | 'ouvrir' | 'sansPapier'> & {
    corps: string;
  },
): boolean {
  return (demande.ouvrir ?? ouvrirDetachee)(assembler(demande, demande.corps));
}

/**
 * Les feuilles de l'application, recopiées DANS le document.
 *
 * Pour le PDF : le serveur le rend sans aucun accès au réseau (voir
 * `apps/api/src/core/pdf.ts`), il ne peut donc pas aller chercher une feuille
 * liée. On la lit ici, depuis le même site, et son texte voyage avec le reste.
 */
async function stylesIntegres(document_: Document): Promise<string> {
  const morceaux = await Promise.all(
    [...document_.querySelectorAll('link[rel="stylesheet"], style')].map(async (noeud) => {
      if (!(noeud instanceof HTMLLinkElement)) return noeud.textContent ?? '';
      try {
        return await (await fetch(noeud.href)).text();
      } catch {
        return '';
      }
    }),
  );
  // Une balise fermante dans une feuille refermerait la nôtre.
  return `<style>${morceaux.join('\n').replace(/<\/style/gi, '<\\/style')}</style>`;
}

/**
 * Le document construit, prêt à être rendu en PDF par le serveur : mêmes
 * styles et même mise en page que l'aperçu, sans le script qui lance
 * l'impression.
 */
export async function documentPourPdf(
  demande: Pick<DemandeImpression, 'titre' | 'orientation' | 'style' | 'sansPapier'> & { corps: string },
): Promise<string> {
  return assembler(demande, demande.corps, { feuilles: await stylesIntegres(document), script: '' });
}

/**
 * Télécharge le document en PDF — la sortie de TOUTES les impressions.
 *
 * Le PDF est rendu par le serveur (voir `apps/api/src/core/pdf.ts`) : même mise
 * en page que l'aperçu, sans la date ni l'adresse que le navigateur ajoute en
 * marge, et un fichier qu'on imprime, archive ou transmet tel quel.
 *
 * Un serveur sans Chromium répond 503 : on retombe alors sur la fenêtre
 * d'impression, plutôt que de laisser l'utilisateur sans rien.
 */
export async function telechargerPdf(
  api: ClientApi,
  demande: Pick<DemandeImpression, 'titre' | 'orientation' | 'style' | 'sansPapier'> & { corps: string; classe?: string },
): Promise<void> {
  const html = await documentPourPdf(demande);
  const envoyer = () => api.telecharger('/impressions/pdf', `${demande.titre}.pdf`, {
    methode: 'POST',
    corps: { titre: demande.titre, html },
    ...(demande.classe ? { classe: demande.classe } : {}),
  });
  for (let essai = 1; ; essai += 1) {
    try {
      await envoyer();
      return;
    } catch (e) {
      // Le proxy a freiné (trop de demandes à la fois) : on patiente et on rejoue.
      if (e instanceof ApiError && e.code === 'RATE_LIMITED' && essai < 4) {
        await new Promise((r) => setTimeout(r, 1500 * essai));
        continue;
      }
      // Le serveur n'a pas de Chromium : la fenêtre d'impression prend le relais.
      if (e instanceof ApiError && e.code === 'SERVICE_UNAVAILABLE' && imprimerDocument(demande)) return;
      throw e;
    }
  }
}

/** Le PDF d'un module, au modèle de la v1 : son titre, son corps, son style. */
export function demandeV1(doc: DocumentV1): Parameters<typeof telechargerPdf>[1] {
  return {
    titre: doc.titre,
    orientation: doc.orientation,
    style: BASE_V1 + doc.style,
    corps: corpsV1(doc),
    sansPapier: true,
  };
}
